"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var react_1 = require("react");
/**
 *  Sets up and safely breaks down an intersection observer that watches an element
 *  for whether it is intersecting with another element (the view portal by default).
 *
 *  Can be used for lazy loading images, triggering animations etc.
 *
 *    @example
 *    // Triggering a fade-in animation based on when a div moves into view.
 *    const [bind, entry] = useIntersect();
 *    const { isIntersecting } = entry
 *
 *    const [fade, setFade] = useSpring(() => {
 *      opacity: 0,
 *    });
 *
 *    useEffect(() => {
 *      if (isIntersecting) setFade({opacity: 1});
 *    }, [isIntersecting, setFade])
 *
 *    return (
 *    <div {...bind}>
 *      <animated.div style={fade}>
 *        <h1>Hello!</h1>
 *      </animated.div>
 *      </div>
 *    )
 */
exports.useIntersect = function (root, threshold, rootMargin) {
    if (root === void 0) { root = null; }
    if (threshold === void 0) { threshold = 0; }
    var _a = react_1.useState({}), entry = _a[0], setEntry = _a[1];
    var _b = react_1.useState(null), ref = _b[0], setRef = _b[1];
    var observer = react_1.useRef(null);
    react_1.useEffect(function () {
        if (observer.current)
            observer.current.disconnect();
        observer.current = new IntersectionObserver(function (_a) {
            var entry = _a[0];
            return setEntry(entry);
        }, {
            root: root,
            rootMargin: rootMargin,
            threshold: threshold,
        });
        var currentObserver = observer.current;
        if (ref)
            currentObserver.observe(ref);
        return function () { return currentObserver.disconnect(); };
    }, [ref, root, rootMargin, threshold]);
    return [{ ref: setRef }, entry];
};
